import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Slider } from '../../components/ui/slider';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Separator } from '../../components/ui/separator';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Cloud, ExternalLink, HardDrive, Users, Zap, Shield, 
  Key, Loader2, AlertCircle, ArrowLeft, Globe,
  TrendingUp, Settings, Lock, User,
  CheckCircle2, Copy, Plus, Minus, ShieldCheck, FileText,
  Calculator, Euro, ArrowRight, XCircle, AlertTriangle, RotateCcw
} from 'lucide-react';

export const PortalInstanz = () => {
  const { instanceId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [instance, setInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [storageModalOpen, setStorageModalOpen] = useState(false);
  const [featureModalOpen, setFeatureModalOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  
  // Password states
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [settingPassword, setSettingPassword] = useState(false);
  const [passwordSet, setPasswordSet] = useState(false);
  
  // Feature change states
  const [pendingChanges, setPendingChanges] = useState({});
  const [savingFeature, setSavingFeature] = useState(false);
  
  // Storage states
  const [selectedStorage, setSelectedStorage] = useState(250);
  const [storageOptions, setStorageOptions] = useState(null);
  const [pricing, setPricing] = useState(null);
  
  // Cancellation states
  const [cancellationInfo, setCancellationInfo] = useState(null);
  const [loadingCancellation, setLoadingCancellation] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [revokingCancellation, setRevokingCancellation] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Load pricing from backend
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/config/pricing`);
        if (response.ok) {
          const data = await response.json();
          setPricing({
            storage_per_50gb: data.storage_extra_50gb,
            collabora_per_user: data.collabora_per_user,
            hp_backend: data.hp_backend,
            antivirus: data.antivirus,
            base_price: data.base_price,
            storage_base_gb: data.storage_base_gb || 250,
            storage_step_gb: data.storage_step_gb || 50
          });
        }
      } catch (err) {
        console.error('Failed to load pricing from API:', err);
        // Keine Fallback-Preise - Preise müssen aus der ENV/API kommen
        setPricing(null);
      }
    };
    fetchPricing();
  }, [backendUrl]);

  useEffect(() => {
    const fetchInstance = async () => {
      if (!instanceId || !token) return;
      
      try {
        const response = await fetch(`${backendUrl}/api/user/instances/${instanceId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) throw new Error('Instanz konnte nicht geladen werden');
        
        const data = await response.json();
        setInstance(data);
        setPasswordSet(data.password_set || false);
        setSelectedStorage(data.storage_gb || 100);
      } catch (err) {
        console.error('Error fetching instance:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInstance();
  }, [instanceId, token, backendUrl]);

  // Calculate price change
  const calculateNewPrice = (changes = {}) => {
    if (!instance) return { current: 0, new: 0, diff: 0 };
    
    const current = instance.monthly_price || pricing?.base_price || 0;
    
    let newPrice = pricing?.base_price || 0;
    
    // Storage (Basis: 250GB, Extra in 50GB-Schritten)
    const storageGb = changes.storage_gb ?? instance.storage_gb ?? 250;
    const baseStorage = pricing?.storage_base_gb || 250;
    const storageStep = pricing?.storage_step_gb || 50;
    const extraStorage = Math.max(0, storageGb - baseStorage);
    newPrice += (extraStorage / storageStep) * (pricing?.storage_per_50gb || 0);
    
    // Collabora
    const collaboraUsers = changes.collabora_users ?? instance.collabora_users ?? 0;
    newPrice += collaboraUsers * (pricing?.collabora_per_user || 0);
    
    // HP Backend
    const hpBackend = changes.hp_backend ?? instance.hp_backend ?? false;
    if (hpBackend) newPrice += pricing?.hp_backend || 0;
    
    // Antivirus
    const antivirus = changes.antivirus ?? instance.antivirus ?? false;
    if (antivirus) newPrice += pricing?.antivirus || 0;
    
    return {
      current: current,
      new: newPrice,
      diff: newPrice - current
    };
  };

  // Load cancellation info
  const fetchCancellationInfo = async () => {
    setLoadingCancellation(true);
    setCancellationInfo(null);
    try {
      const response = await fetch(`${backendUrl}/api/portal/instances/${instanceId}/cancellation-info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setCancellationInfo(data);
      } else {
        console.error('Cancellation info error:', data);
        // Set error info so the modal can show it
        setCancellationInfo({ error: data.detail || 'Fehler beim Laden', can_cancel: false });
      }
    } catch (err) {
      console.error('Error loading cancellation info:', err);
      setCancellationInfo({ error: 'Verbindungsfehler', can_cancel: false });
    } finally {
      setLoadingCancellation(false);
    }
  };

  // Handle cancel subscription
  const handleCancelSubscription = async () => {
    setCancellingSubscription(true);
    try {
      const response = await fetch(`${backendUrl}/api/portal/instances/${instanceId}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: cancellationReason })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Kündigung erfolgreich beantragt');
        setCancelModalOpen(false);
        setCancellationReason('');
        // Refresh instance and cancellation info
        window.location.reload();
      } else {
        toast.error(data.detail || 'Fehler bei der Kündigung');
      }
    } catch (err) {
      toast.error('Verbindungsfehler');
    } finally {
      setCancellingSubscription(false);
    }
  };

  // Handle revoke cancellation
  const handleRevokeCancellation = async () => {
    setRevokingCancellation(true);
    try {
      const response = await fetch(`${backendUrl}/api/portal/instances/${instanceId}/revoke-cancellation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Kündigung widerrufen');
        // Refresh instance and cancellation info
        window.location.reload();
      } else {
        toast.error(data.detail || 'Fehler beim Widerrufen');
      }
    } catch (err) {
      toast.error('Verbindungsfehler');
    } finally {
      setRevokingCancellation(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In Zwischenablage kopiert');
  };

  // Password handler - generates password via SCAPI and shows in modal
  const handleGenerateInitialPassword = async () => {
    setSettingPassword(true);
    
    try {
      const response = await fetch(`${backendUrl}/api/portal/instances/${instanceId}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Passwort konnte nicht generiert werden');
      }
      
      const data = await response.json();
      
      if (data.admin_password) {
        setGeneratedPassword(data.admin_password);
        setPasswordSet(true);
        setPasswordModalOpen(true);
        setInstance(prev => ({ ...prev, admin_password: data.admin_password }));
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSettingPassword(false);
    }
  };

  // Feature change handler
  const handleFeatureChange = async () => {
    if (!activeFeature || Object.keys(pendingChanges).length === 0) return;
    
    setSavingFeature(true);
    
    try {
      // TODO: API call to update features
      const response = await fetch(`${backendUrl}/api/portal/instances/${instanceId}/features`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pendingChanges)
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Feature konnte nicht geändert werden');
      }
      
      // Update local state
      setInstance(prev => ({ ...prev, ...pendingChanges }));
      toast.success('Änderungen gespeichert!');
      setFeatureModalOpen(false);
      setPendingChanges({});
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingFeature(false);
    }
  };

  // Storage change handler
  const handleStorageChange = async () => {
    if (selectedStorage === instance.storage_gb) {
      setStorageModalOpen(false);
      return;
    }
    
    setSavingFeature(true);
    
    try {
      const response = await fetch(`${backendUrl}/api/portal/instances/${instanceId}/storage`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ storage_gb: selectedStorage })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Speicher konnte nicht geändert werden');
      }
      
      setInstance(prev => ({ ...prev, storage_gb: selectedStorage }));
      toast.success(`Speicher auf ${selectedStorage} GB geändert!`);
      setStorageModalOpen(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingFeature(false);
    }
  };

  // Open feature modal
  const openFeatureModal = (feature) => {
    setActiveFeature(feature);
    setPendingChanges({});
    setFeatureModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-state">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !instance) {
    return (
      <div className="text-center py-12" data-testid="error-state">
        <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Fehler beim Laden</h2>
        <p className="text-muted-foreground mb-4">{error || 'Instanz nicht gefunden'}</p>
        <Button asChild>
          <Link to="/portal/wolken">Zurück zu meinen Wolken</Link>
        </Button>
      </div>
    );
  }

  const isReady = instance.status === 'ready';
  const storageUsedPercent = instance.storage_used_bytes 
    ? Math.round((instance.storage_used_bytes / (instance.storage_gb * 1024 * 1024 * 1024)) * 100) 
    : 0;

  const priceInfo = calculateNewPrice();

  return (
    <div className="space-y-6" data-testid="portal-instanz">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/portal/wolken">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
              <Cloud className="h-6 w-6 text-primary" />
              {instance.name || instance.domain}
            </h1>
            <p className="text-muted-foreground text-sm">{instance.domain}</p>
          </div>
          <Badge variant={isReady ? 'default' : 'secondary'} className={isReady ? 'bg-green-500' : ''}>
            {isReady ? 'Online' : instance.status}
          </Badge>
        </div>
        {isReady && instance.url && (
          <Button asChild className="bg-gradient-to-r from-primary to-blue-600">
            <a href={instance.url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-2" />
              Cloud öffnen
            </a>
          </Button>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Status & Storage */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HardDrive className="h-5 w-5 text-primary" />
              Speicher
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">Verwendet</span>
                <span className="font-medium">
                  {instance.storage_used_bytes 
                    ? `${(instance.storage_used_bytes / (1024 * 1024 * 1024)).toFixed(1)} GB` 
                    : '0 GB'} 
                  / {instance.storage_gb} GB
                </span>
              </div>
              <Progress value={storageUsedPercent} className="h-3" />
              <p className="text-xs text-muted-foreground mt-1">{storageUsedPercent}% belegt</p>
            </div>
            
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => {
                setSelectedStorage(instance.storage_gb);
                setStorageModalOpen(true);
              }}
            >
              <HardDrive className="h-4 w-4 mr-2" />
              Speicher anpassen
            </Button>
          </CardContent>
        </Card>

        {/* Access Information */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-primary" />
              Zugang
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-muted-foreground text-xs">Cloud-URL</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 bg-muted px-3 py-2 rounded text-sm truncate">
                  {instance.url || `https://${instance.domain}`}
                </code>
                <Button variant="outline" size="sm" onClick={() => copyToClipboard(instance.url || `https://${instance.domain}`)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-muted-foreground text-xs">Admin-E-Mail</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 bg-muted px-3 py-2 rounded text-sm truncate">
                  {instance.admin_email}
                </code>
                <Button variant="outline" size="sm" onClick={() => copyToClipboard(instance.admin_email)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {isReady && (
              <Button 
                variant="outline" 
                className="w-full"
                onClick={handleGenerateInitialPassword}
                disabled={settingPassword}
                data-testid="initial-password-btn"
              >
                {settingPassword ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Key className="h-4 w-4 mr-2" />
                )}
                {passwordSet ? 'Neues Initial-Passwort erstellen' : 'Initial-Passwort erstellen'}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Notfall-Domain */}
      {instance.internal_domain && (
        <Card className="glass-card border-border">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Globe className="h-5 w-5 text-primary" />
              Notfall-Domain
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-muted px-3 py-2 rounded text-sm truncate font-mono">
                https://{instance.internal_domain}
              </code>
              <Button variant="outline" size="sm" onClick={() => copyToClipboard(`https://${instance.internal_domain}`)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-3 rounded-lg bg-muted/50 border border-border">
              <p className="text-xs text-muted-foreground leading-relaxed">
                <strong>Wichtig:</strong> Die Erreichbarkeit deiner Cloud wird anhand dieser Notfall-Domain gemessen. 
                Sie ist unabhängig von den DNS-Einstellungen deiner Custom-Domain und stellt sicher, 
                dass deine Cloud auch bei Änderungen an der eigenen Domain (z.B. durch Dritte) 
                erreichbar bleibt. Für Einschränkungen durch Fremdeinwirkungen auf deine DNS-Einstellungen 
                übernehmen wir keine Verantwortung (siehe AGB).
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Features Grid */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Features & Add-ons
          </CardTitle>
          <CardDescription>
            Erweitere deine Helden-Wolke mit zusätzlichen Features.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Antivirus */}
            <div 
              className={`p-4 rounded-lg border cursor-pointer transition-all hover:border-primary/50 ${instance.antivirus ? 'bg-green-500/5 border-green-500/30' : ''}`}
              onClick={() => openFeatureModal('antivirus')}
            >
              <div className="flex items-center justify-between mb-2">
                <ShieldCheck className={`h-6 w-6 ${instance.antivirus ? 'text-green-500' : 'text-muted-foreground'}`} />
                {instance.antivirus && <Badge className="bg-green-500">Aktiv</Badge>}
              </div>
              <h4 className="font-medium">Antivirus</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Automatischer Virenscan
              </p>
              <p className="text-sm font-medium text-primary mt-2">
                +{(pricing?.antivirus || 0).toFixed(2)} €/Monat
              </p>
            </div>

            {/* Collabora */}
            <div 
              className={`p-4 rounded-lg border cursor-pointer transition-all hover:border-primary/50 ${instance.collabora_users > 0 ? 'bg-blue-500/5 border-blue-500/30' : ''}`}
              onClick={() => openFeatureModal('collabora')}
            >
              <div className="flex items-center justify-between mb-2">
                <Users className={`h-6 w-6 ${instance.collabora_users > 0 ? 'text-blue-500' : 'text-muted-foreground'}`} />
                {instance.collabora_users > 0 && <Badge className="bg-blue-500">{instance.collabora_users} User</Badge>}
              </div>
              <h4 className="font-medium">Collabora Office</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Online Office Suite
              </p>
              <p className="text-sm font-medium text-primary mt-2">
                +{(pricing?.collabora_per_user || 0).toFixed(2)} €/User/Monat
              </p>
            </div>

            {/* HP Backend */}
            <div 
              className={`p-4 rounded-lg border cursor-pointer transition-all hover:border-primary/50 ${instance.hp_backend ? 'bg-amber-500/5 border-amber-500/30' : ''}`}
              onClick={() => openFeatureModal('hp_backend')}
            >
              <div className="flex items-center justify-between mb-2">
                <Zap className={`h-6 w-6 ${instance.hp_backend ? 'text-amber-500' : 'text-muted-foreground'}`} />
                {instance.hp_backend && <Badge className="bg-amber-500">Aktiv</Badge>}
              </div>
              <h4 className="font-medium">HPB für Talk</h4>
              <p className="text-sm text-muted-foreground mt-1">
                High Performance Backend
              </p>
              <p className="text-sm font-medium text-primary mt-2">
                +{(pricing?.hp_backend || 0).toFixed(2)} €/Monat
              </p>
            </div>

            {/* Storage Info */}
            <div 
              className="p-4 rounded-lg border cursor-pointer transition-all hover:border-primary/50 bg-primary/5 border-primary/30"
              onClick={() => {
                setSelectedStorage(instance.storage_gb);
                setStorageModalOpen(true);
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <HardDrive className="h-6 w-6 text-primary" />
                <Badge className="bg-primary">{instance.storage_gb} GB</Badge>
              </div>
              <h4 className="font-medium">Speicherplatz</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Cloud-Speicher
              </p>
              <p className="text-sm font-medium text-primary mt-2">
                +{pricing?.storage_per_50gb?.toFixed(2) || '1.18'} €/50GB
              </p>
            </div>
          </div>
          
          <Separator className="my-6" />
          
          {/* Price Summary */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-gradient-to-r from-primary/10 to-blue-500/10 border border-primary/20">
            <div className="flex items-center gap-3">
              <Euro className="h-6 w-6 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Monatliche Kosten</p>
                <p className="text-2xl font-bold text-primary">
                  {(instance.monthly_price || priceInfo.current).toFixed(2)} €
                </p>
              </div>
            </div>
            <Button variant="outline" asChild>
              <Link to="/portal/rechnungen">
                <FileText className="h-4 w-4 mr-2" />
                Rechnungen
              </Link>
            </Button>
          </div>
          
          <Separator className="my-6" />
          
          {/* Cancellation Section */}
          <div className="p-4 rounded-lg border border-red-500/20 bg-red-500/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <XCircle className="h-6 w-6 text-red-500/70" />
                <div>
                  <p className="font-medium text-foreground">Kündigung</p>
                  {instance.cancellation_pending ? (
                    <p className="text-sm text-red-500">
                      Gekündigt zum {instance.cancellation_date ? new Date(instance.cancellation_date).toLocaleDateString('de-DE') : 'N/A'}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Cloud zum Ende der Laufzeit kündigen
                    </p>
                  )}
                </div>
              </div>
              {instance.cancellation_pending ? (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleRevokeCancellation}
                  disabled={revokingCancellation}
                  className="border-green-500/50 text-green-600 hover:bg-green-500/10"
                >
                  {revokingCancellation ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4 mr-2" />
                  )}
                  Widerrufen
                </Button>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    fetchCancellationInfo();
                    setCancelModalOpen(true);
                  }}
                  className="border-red-500/50 text-red-600 hover:bg-red-500/10"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Kündigen
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ==================== MODALS ==================== */}

      {/* Cancellation Modal */}
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Cloud kündigen
            </DialogTitle>
            <DialogDescription>
              Bist du sicher, dass du deine Cloud kündigen möchtest?
            </DialogDescription>
          </DialogHeader>
          
          {loadingCancellation ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : cancellationInfo?.error ? (
            <div className="py-8 text-center">
              <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
              <p className="text-red-500 font-medium">{cancellationInfo.error}</p>
              <p className="text-muted-foreground text-sm mt-2">
                Bitte versuche es später erneut oder kontaktiere den Support.
              </p>
            </div>
          ) : cancellationInfo ? (
            <div className="space-y-4 py-4">
              {/* Info Box */}
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                  <div className="space-y-2 text-sm">
                    {cancellationInfo.can_cancel_for_current_period ? (
                      <p className="text-amber-700 dark:text-amber-400">
                        <strong>Kündigung möglich zum Ende der aktuellen Laufzeit.</strong>
                      </p>
                    ) : (
                      <p className="text-amber-700 dark:text-amber-400">
                        <strong>Weniger als 24h bis zur Verlängerung.</strong> Kündigung erst zum übernächsten Zeitraum möglich.
                      </p>
                    )}
                    <p className="text-muted-foreground">
                      Deine Cloud wird am <strong>{new Date(cancellationInfo.cancellation_effective_date).toLocaleDateString('de-DE')}</strong> deaktiviert.
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Stunden bis Verlängerung: {cancellationInfo.hours_until_renewal}h
                    </p>
                  </div>
                </div>
              </div>
              
              {/* What happens */}
              <div className="space-y-2 text-sm">
                <p className="font-medium">Was passiert bei der Kündigung:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-2">
                  <li>Die Cloud bleibt bis zum Kündigungsdatum voll nutzbar</li>
                  <li>Am Kündigungsdatum wird die Cloud automatisch deaktiviert</li>
                  <li>Alle Daten werden unwiderruflich gelöscht</li>
                  <li>Die Kündigung kann bis zum Kündigungstag widerrufen werden</li>
                </ul>
              </div>
              
              {/* Reason (optional) */}
              <div className="space-y-2">
                <Label htmlFor="cancel-reason">Kündigungsgrund (optional)</Label>
                <Textarea
                  id="cancel-reason"
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  placeholder="Warum möchtest du kündigen? Dein Feedback hilft uns..."
                  rows={3}
                />
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              Kündigungsinformationen konnten nicht geladen werden.
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelModalOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={cancellingSubscription || !cancellationInfo?.can_cancel}
            >
              {cancellingSubscription ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              Kündigung bestätigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Modal - shows generated password */}
      <Dialog open={passwordModalOpen} onOpenChange={(open) => {
        setPasswordModalOpen(open);
        if (!open) setGeneratedPassword(null);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-primary" />
              Initial-Passwort erstellt
            </DialogTitle>
            <DialogDescription>
              Das Passwort wurde generiert und in deiner Cloud gesetzt.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg bg-muted/50 border">
              <Label className="text-xs text-muted-foreground">Benutzername</Label>
              <div className="flex items-center gap-2 mt-2">
                <code className="flex-1 bg-background px-4 py-3 rounded text-base font-mono font-semibold">
                  admin
                </code>
                <Button variant="outline" size="sm" onClick={() => copyToClipboard('admin')}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {generatedPassword && (
              <div className="p-4 rounded-lg bg-muted/50 border">
                <Label className="text-xs text-muted-foreground">Dein Initial-Passwort</Label>
                <div className="flex items-center gap-2 mt-2">
                  <code className="flex-1 bg-background px-4 py-3 rounded text-base font-mono font-semibold select-all">
                    {generatedPassword}
                  </code>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(generatedPassword)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <div className="flex gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-amber-700">Wichtiger Hinweis</p>
                  <p className="text-sm text-amber-600">
                    Dies ist ein Initial-Passwort. Bitte ändere es <strong>umgehend</strong> nach dem ersten Login direkt in deiner Nextcloud unter <em>Einstellungen &rarr; Sicherheit</em>.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              onClick={() => {
                setPasswordModalOpen(false);
                setGeneratedPassword(null);
              }}
              className="w-full"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Verstanden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Storage Modal */}
      <Dialog open={storageModalOpen} onOpenChange={setStorageModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HardDrive className="h-5 w-5 text-primary" />
              Speicherplatz anpassen
            </DialogTitle>
            <DialogDescription>
              Erhöhe oder verringere deinen Cloud-Speicher.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Current usage */}
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex justify-between text-sm mb-2">
                <span>Aktuell verwendet</span>
                <span className="font-medium">
                  {instance.storage_used_bytes 
                    ? `${(instance.storage_used_bytes / (1024 * 1024 * 1024)).toFixed(1)} GB` 
                    : '0 GB'}
                </span>
              </div>
              <Progress value={storageUsedPercent} className="h-2" />
            </div>
            
            {/* Storage selector */}
            <div>
              <Label className="text-base font-medium">Neuer Speicherplatz</Label>
              <div className="flex items-center gap-4 mt-4">
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setSelectedStorage(Math.max(250, selectedStorage - 50))}
                  disabled={selectedStorage <= 250}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <div className="flex-1 text-center">
                  <span className="text-4xl font-bold text-primary">{selectedStorage}</span>
                  <span className="text-xl text-muted-foreground ml-1">GB</span>
                </div>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setSelectedStorage(Math.min(2000, selectedStorage + 50))}
                  disabled={selectedStorage >= 2000}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              <Slider
                value={[selectedStorage]}
                onValueChange={(val) => setSelectedStorage(val[0])}
                min={250}
                max={2000}
                step={50}
                className="mt-4"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>250 GB</span>
                <span>2000 GB</span>
              </div>
            </div>
            
            {/* Price calculation */}
            <div className="p-4 rounded-lg border border-primary/30 bg-primary/5">
              <div className="flex items-center gap-2 mb-3">
                <Calculator className="h-5 w-5 text-primary" />
                <span className="font-medium">Preisänderung</span>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Basis (250 GB inkl.)</span>
                  <span>{(pricing?.base_price || 0).toFixed(2)} €</span>
                </div>
                {selectedStorage > 250 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">+ {selectedStorage - 250} GB extra</span>
                    <span>+{(((selectedStorage - 250) / 50) * (pricing?.storage_per_50gb || 0)).toFixed(2)} €</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-medium text-base">
                  <span>Neuer Monatspreis</span>
                  <span className="text-primary">
                    {calculateNewPrice({ storage_gb: selectedStorage }).new.toFixed(2)} €
                  </span>
                </div>
                {selectedStorage !== instance.storage_gb && (
                  <div className={`flex justify-between text-sm ${calculateNewPrice({ storage_gb: selectedStorage }).diff > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                    <span>Differenz</span>
                    <span>
                      {calculateNewPrice({ storage_gb: selectedStorage }).diff > 0 ? '+' : ''}
                      {calculateNewPrice({ storage_gb: selectedStorage }).diff.toFixed(2)} €/Monat
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setStorageModalOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleStorageChange}
              disabled={savingFeature || selectedStorage === instance.storage_gb}
            >
              {savingFeature ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Änderung speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feature Modal */}
      <Dialog open={featureModalOpen} onOpenChange={setFeatureModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {activeFeature === 'antivirus' && <ShieldCheck className="h-5 w-5 text-green-500" />}
              {activeFeature === 'collabora' && <Users className="h-5 w-5 text-blue-500" />}
              {activeFeature === 'hp_backend' && <Zap className="h-5 w-5 text-amber-500" />}
              {activeFeature === 'antivirus' && 'Antivirus'}
              {activeFeature === 'collabora' && 'Collabora Office'}
              {activeFeature === 'hp_backend' && 'HPB für Talk'}
            </DialogTitle>
            <DialogDescription>
              {activeFeature === 'antivirus' && 'Automatischer Virenscan für alle hochgeladenen Dateien.'}
              {activeFeature === 'collabora' && 'Bearbeite Office-Dokumente direkt in deiner Cloud.'}
              {activeFeature === 'hp_backend' && 'High Performance Backend für kristallklare Video- und Audio-Calls.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Antivirus Toggle */}
            {activeFeature === 'antivirus' && (
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div>
                  <h4 className="font-medium">Antivirus aktivieren</h4>
                  <p className="text-sm text-muted-foreground">+{(pricing?.antivirus || 0).toFixed(2)} €/Monat</p>
                </div>
                <Switch 
                  checked={pendingChanges.antivirus ?? instance.antivirus ?? false}
                  onCheckedChange={(checked) => setPendingChanges({ antivirus: checked })}
                />
              </div>
            )}
            
            {/* Collabora Users */}
            {activeFeature === 'collabora' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Anzahl Collabora-Nutzer</Label>
                </div>
                <div className="flex items-center gap-4">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setPendingChanges({ 
                      collabora_users: Math.max(0, (pendingChanges.collabora_users ?? instance.collabora_users ?? 0) - 1) 
                    })}
                    disabled={(pendingChanges.collabora_users ?? instance.collabora_users ?? 0) <= 0}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <div className="flex-1 text-center">
                    <span className="text-4xl font-bold text-primary">
                      {pendingChanges.collabora_users ?? instance.collabora_users ?? 0}
                    </span>
                    <span className="text-xl text-muted-foreground ml-1">User</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setPendingChanges({ 
                      collabora_users: Math.min(100, (pendingChanges.collabora_users ?? instance.collabora_users ?? 0) + 1) 
                    })}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  {(pricing?.collabora_per_user || 0).toFixed(2)} € pro Nutzer/Monat
                </p>
              </div>
            )}
            
            {/* HP Backend Toggle */}
            {activeFeature === 'hp_backend' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div>
                    <h4 className="font-medium">HPB für Talk aktivieren</h4>
                    <p className="text-sm text-muted-foreground">+{(pricing?.hp_backend || 0).toFixed(2)} €/Monat</p>
                  </div>
                  <Switch 
                    checked={pendingChanges.hp_backend ?? instance.hp_backend ?? false}
                    onCheckedChange={(checked) => setPendingChanges({ hp_backend: checked })}
                  />
                </div>
                
                <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <h5 className="font-medium flex items-center gap-2 text-amber-600">
                    <Zap className="h-4 w-4" />
                    Was bringt das High Performance Backend?
                  </h5>
                  <ul className="mt-2 text-sm text-muted-foreground space-y-1">
                    <li>• Kristallklare Video- und Audio-Qualität in Nextcloud Talk</li>
                    <li>• Geringere Latenz bei Calls und Konferenzen</li>
                    <li>• Mehr Teilnehmer in Video-Meetings möglich</li>
                    <li>• Optimiert für Screen-Sharing und Präsentationen</li>
                  </ul>
                </div>
              </div>
            )}
            
            {/* Price calculation */}
            <div className="p-4 rounded-lg border border-primary/30 bg-primary/5">
              <div className="flex items-center gap-2 mb-3">
                <Calculator className="h-5 w-5 text-primary" />
                <span className="font-medium">Preisänderung</span>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Aktueller Preis</span>
                  <span>{(instance.monthly_price || pricing?.base_price || 0).toFixed(2)} €</span>
                </div>
                <Separator />
                <div className="flex justify-between font-medium text-base">
                  <span>Neuer Monatspreis</span>
                  <span className="text-primary">
                    {calculateNewPrice(pendingChanges).new.toFixed(2)} €
                  </span>
                </div>
                {Object.keys(pendingChanges).length > 0 && (
                  <div className={`flex justify-between text-sm ${calculateNewPrice(pendingChanges).diff > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                    <span>Differenz</span>
                    <span>
                      {calculateNewPrice(pendingChanges).diff > 0 ? '+' : ''}
                      {calculateNewPrice(pendingChanges).diff.toFixed(2)} €/Monat
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeatureModalOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleFeatureChange}
              disabled={savingFeature || Object.keys(pendingChanges).length === 0}
            >
              {savingFeature ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Änderung speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
